import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandShortcut,
} from "./ui/command";
import { DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { MODULE_SCHEMAS, MODULE_ORDER } from "../lib/moduleSchemas";
import { Search } from "lucide-react";

export default function QuickSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query.trim() } });
        setResults(data);
      } catch {
        setResults([]);
      }
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const go = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-8 gap-2 text-slate-500 pr-2 pl-2.5"
        data-testid="quick-search-button"
      >
        <Search className="h-3.5 w-3.5" strokeWidth={2} />
        <span className="hidden sm:inline text-xs">Search...</span>
        <kbd className="hidden md:inline-flex h-5 items-center rounded border border-slate-200 bg-slate-50 px-1.5 font-mono text-[10px] text-slate-500">
          ⌘K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <DialogTitle className="sr-only">Quick search</DialogTitle>
        <DialogDescription className="sr-only">
          Search your vault entries, modules and pages
        </DialogDescription>
        <CommandInput
          placeholder="Search entries, modules, pages..."
          value={query}
          onValueChange={setQuery}
          data-testid="quick-search-input"
        />
        <CommandList>
          <CommandEmpty>
            {query.trim() ? "No matches found." : "Type to search across your vault."}
          </CommandEmpty>

          {results.length > 0 && (
            <CommandGroup heading="Entries">
              {results.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`${r.title}-${r.id}`}
                  onSelect={() => go(`/m/${r.module}`)}
                  data-testid={`quick-search-result-${r.id}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{r.title}</span>
                    <span className="text-xs text-slate-500">
                      {MODULE_SCHEMAS[r.module]?.label || r.module}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          <CommandGroup heading="Jump to module">
            {MODULE_ORDER.map((slug) => (
              <CommandItem
                key={slug}
                value={`goto-${slug}`}
                onSelect={() => go(`/m/${slug}`)}
              >
                <span>{MODULE_SCHEMAS[slug].label}</span>
                <CommandShortcut className="text-[10px] uppercase">{slug}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandGroup heading="Pages">
            <CommandItem onSelect={() => go("/")}>Dashboard</CommandItem>
            <CommandItem onSelect={() => go("/nominees")}>Nominee Tracker</CommandItem>
            <CommandItem onSelect={() => go("/emergency-plan")}>Emergency Plan</CommandItem>
            <CommandItem onSelect={() => go("/emergency-access")}>Emergency Access</CommandItem>
            <CommandItem onSelect={() => go("/vault")}>Document Vault</CommandItem>
            <CommandItem onSelect={() => go("/import")}>Import Data</CommandItem>
            <CommandItem onSelect={() => go("/reports")}>Reports & Exports</CommandItem>
            <CommandItem onSelect={() => go("/settings")}>Settings</CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
